/**
 * ZicassoBestPractices — página autónoma con las normas de Zicasso
 *   1. Timeline oficial (10 steps)
 *   2. Reglas de oro
 *   3. Templates con preview HTML
 *   4. Sección "Ideas de acciones y automatizaciones" (CRUD compartido)
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axios from "axios";
import {
  ListChecks, Lightbulb, ScrollText, Sparkles, Plus, Trash2, X,
  Check, Clock, ArrowUpRight, Pencil,
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("sofi_token") || ""}` });

const sanitizeHtml = (html = "") =>
  (html || "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "");

const htmlToText = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return div.innerText || "";
};

const PRIORITY_COLORS = {
  high: "border-red-300 bg-red-50 text-red-900",
  medium: "border-amber-300 bg-amber-50 text-amber-900",
  low: "border-slate-200 bg-slate-50 text-slate-800",
};
const STATUS_META = {
  idea: { label: "Idea", color: "bg-slate-100 text-slate-700 border-slate-300" },
  approved: { label: "Aprobada", color: "bg-blue-100 text-blue-700 border-blue-300" },
  in_progress: { label: "En curso", color: "bg-amber-100 text-amber-800 border-amber-300" },
  implemented: { label: "Implementada", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  rejected: { label: "Descartada", color: "bg-slate-100 text-slate-500 border-slate-300" },
};

function TimelineSection({ timeline }) {
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
          <ListChecks className="h-5 w-5 text-primary" />
          Timeline oficial de Zicasso (10 pasos)
        </h3>
        <div className="space-y-2" data-testid="bp-timeline">
          {timeline.map((step) => (
            <div
              key={step.step}
              className={`flex gap-3 rounded-md border p-3 ${PRIORITY_COLORS[step.priority]}`}
              data-testid={`bp-step-${step.step}`}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-lg font-bold shadow">
                {step.step}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {step.day}
                  </span>
                  <Badge variant="outline" className="h-5 text-[10px]">
                    {step.channel === "call" ? "📞 Llamada" : "💬 Mensaje"}
                  </Badge>
                  <Badge variant="outline" className={`h-5 text-[10px] ${PRIORITY_COLORS[step.priority]}`}>
                    Prioridad {step.priority}
                  </Badge>
                </div>
                <div className="mt-1 text-sm font-semibold">{step.action}</div>
                <div className="mt-1 text-xs leading-relaxed opacity-80">{step.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function RulesSection({ rules, onProposeAutomation }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-bold">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Reglas de oro ({rules.length})
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Click en una regla → propone una automatización basada en ella
          </p>
        </div>
        <div className="grid gap-2 md:grid-cols-2" data-testid="bp-rules">
          {rules.map((rule, i) => (
            <button
              key={i}
              onClick={() => onProposeAutomation(rule)}
              className="rounded-md border-l-4 border-amber-400 bg-amber-50/30 p-3 text-left hover:bg-amber-50 hover:shadow-sm transition-all"
              data-testid={`bp-rule-${i}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{rule.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{rule.text}</div>
                </div>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-amber-600" />
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TemplatesSection({ templates }) {
  const [selected, setSelected] = useState(null);
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="mb-4 flex items-center gap-2 text-lg font-bold">
          <ScrollText className="h-5 w-5 text-primary" />
          Templates disponibles ({templates.length})
        </h3>
        <div className="grid gap-2 md:grid-cols-3" data-testid="bp-templates">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelected(t)}
              className={`rounded-md border p-3 text-left hover:bg-slate-50 ${
                selected?.id === t.id ? "border-primary bg-primary/5" : ""
              }`}
              data-testid={`bp-template-${t.id}`}
            >
              <div className="text-sm font-semibold">{t.title}</div>
              <div className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">
                {htmlToText(t.body).substring(0, 100)}...
              </div>
            </button>
          ))}
        </div>

        {selected && (
          <div className="mt-4 rounded-md border-2 border-primary/30 bg-slate-50 p-4">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-base font-bold">{selected.title}</h4>
              <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
            <div
              className="prose prose-sm max-w-none text-sm leading-relaxed
                         [&_p]:my-1 [&_a]:text-blue-600 [&_a]:underline
                         [&_ul]:my-1 [&_ol]:my-1 [&_ul]:pl-5 [&_ol]:pl-5
                         [&_strong]:font-semibold [&_li]:my-0.5"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(selected.body) }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AutomationIdeaDialog({ open, onClose, initial, onSave }) {
  const [form, setForm] = useState(() => initial || {
    title: "",
    description: "",
    priority: "medium",
    status: "idea",
    linked_rule: null,
  });

  React.useEffect(() => {
    setForm(initial || {
      title: "",
      description: "",
      priority: "medium",
      status: "idea",
      linked_rule: null,
    });
  }, [initial, open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="automation-idea-dialog">
        <DialogTitle>{initial?.id ? "Editar idea" : "Nueva idea de automatización"}</DialogTitle>
        <DialogDescription>
          Describe la automatización que quieres proponer. Luego podré implementarla.
        </DialogDescription>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold">Título</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ej: Auto-marcar INACTIVE tras 4 meses sin respuesta"
              data-testid="idea-title-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold">Descripción</label>
            <Textarea
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Cómo debería funcionar la automatización, qué disparadores, qué acciones..."
              data-testid="idea-description-textarea"
            />
          </div>
          {form.linked_rule && (
            <div className="rounded-md border-l-2 border-amber-400 bg-amber-50 p-2 text-xs">
              <span className="font-semibold text-amber-900">Basada en regla:</span> {form.linked_rule}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold">Prioridad</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="idea-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="medium">Media</SelectItem>
                  <SelectItem value="low">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold">Estado</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="idea-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="idea">Idea</SelectItem>
                  <SelectItem value="approved">Aprobada</SelectItem>
                  <SelectItem value="in_progress">En curso</SelectItem>
                  <SelectItem value="implemented">Implementada</SelectItem>
                  <SelectItem value="rejected">Descartada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() => onSave(form)}
            disabled={!form.title.trim()}
            data-testid="idea-save-button"
          >
            <Check className="mr-1 h-3 w-3" /> Guardar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IdeaCard({ idea, onEdit, onDelete }) {
  const status = STATUS_META[idea.status] || STATUS_META.idea;
  return (
    <div
      className={`rounded-md border p-3 hover:shadow-sm ${
        idea.status === "implemented" ? "opacity-60" : ""
      }`}
      data-testid={`idea-card-${idea.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{idea.title}</span>
            <Badge className={`h-4 border text-[9px] ${status.color}`}>{status.label}</Badge>
            <Badge variant="outline" className={`h-4 text-[9px] ${PRIORITY_COLORS[idea.priority]}`}>
              {idea.priority}
            </Badge>
          </div>
          {idea.description && (
            <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">
              {idea.description}
            </p>
          )}
          {idea.linked_rule && (
            <p className="mt-1 text-[10px] text-amber-700">
              💡 Basada en: <em>{idea.linked_rule}</em>
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-1">
          <Button size="sm" variant="ghost" onClick={() => onEdit(idea)} data-testid={`edit-idea-${idea.id}`}>
            <Pencil className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(idea.id)} data-testid={`delete-idea-${idea.id}`}>
            <Trash2 className="h-3 w-3 text-red-500" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function IdeasSection() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const ideas = useQuery({
    queryKey: ["zicasso-automation-ideas"],
    queryFn: async () => (await axios.get(`${API}/api/zicasso/automation-ideas`,
      { headers: authHeaders() })).data,
  });

  const saveMut = useMutation({
    mutationFn: (form) => {
      if (form.id) {
        return axios.put(`${API}/api/zicasso/automation-ideas/${form.id}`, form,
          { headers: authHeaders() });
      }
      return axios.post(`${API}/api/zicasso/automation-ideas`, form,
        { headers: authHeaders() });
    },
    onSuccess: () => {
      toast.success("Idea guardada");
      qc.invalidateQueries({ queryKey: ["zicasso-automation-ideas"] });
      setDialogOpen(false);
      setEditing(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error guardando"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => axios.delete(`${API}/api/zicasso/automation-ideas/${id}`,
      { headers: authHeaders() }),
    onSuccess: () => {
      toast.success("Idea eliminada");
      qc.invalidateQueries({ queryKey: ["zicasso-automation-ideas"] });
    },
  });

  const openNew = (initialFromRule = null) => {
    setEditing(initialFromRule
      ? { title: `Automatizar: ${initialFromRule.title}`,
          description: `Basado en la regla Zicasso "${initialFromRule.title}":\n\n${initialFromRule.text}\n\n→ Propuesta de automatización:`,
          priority: "medium", status: "idea", linked_rule: initialFromRule.title }
      : null);
    setDialogOpen(true);
  };

  const items = ideas.data?.items || [];
  // Agrupar por estado
  const byStatus = ["idea", "approved", "in_progress", "implemented", "rejected"]
    .map((k) => ({ key: k, meta: STATUS_META[k], items: items.filter((i) => i.status === k) }))
    .filter((g) => g.items.length > 0);

  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-bold">
              <Sparkles className="h-5 w-5 text-primary" />
              Ideas de acciones y automatizaciones
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Anota aquí las propuestas de automatización. Yo las implementaré según prioridad.
            </p>
          </div>
          <Button size="sm" onClick={() => openNew()} data-testid="new-idea-button">
            <Plus className="mr-1 h-3 w-3" /> Nueva idea
          </Button>
        </div>

        {ideas.isLoading ? (
          <Skeleton className="h-32" />
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
            <Lightbulb className="mx-auto mb-2 h-8 w-8 text-amber-500" />
            <p>Aún no hay ideas propuestas.</p>
            <p className="mt-1 text-[11px]">Click en una regla de arriba o pulsa &quot;Nueva idea&quot;</p>
          </div>
        ) : (
          <div className="space-y-4">
            {byStatus.map((g) => (
              <div key={g.key}>
                <div className={`mb-2 inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${g.meta.color}`}>
                  {g.meta.label} ({g.items.length})
                </div>
                <div className="space-y-2" data-testid={`ideas-group-${g.key}`}>
                  {g.items.map((idea) => (
                    <IdeaCard
                      key={idea.id}
                      idea={idea}
                      onEdit={(x) => { setEditing(x); setDialogOpen(true); }}
                      onDelete={(id) => window.confirm("¿Eliminar esta idea?") && deleteMut.mutate(id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <AutomationIdeaDialog
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setEditing(null); }}
          initial={editing}
          onSave={(form) => saveMut.mutate({ ...form, id: editing?.id })}
        />
      </CardContent>
    </Card>
  );
}

export default function ZicassoBestPractices({ onProposeIdea }) {
  const bp = useQuery({
    queryKey: ["zicasso-best-practices"],
    queryFn: async () => (await axios.get(`${API}/api/zicasso/best-practices`,
      { headers: authHeaders() })).data,
    staleTime: 30 * 60_000,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialForm, setInitialForm] = useState(null);
  const qc = useQueryClient();

  const saveMut = useMutation({
    mutationFn: (form) => axios.post(`${API}/api/zicasso/automation-ideas`, form,
      { headers: authHeaders() }),
    onSuccess: () => {
      toast.success("Idea guardada");
      qc.invalidateQueries({ queryKey: ["zicasso-automation-ideas"] });
      setDialogOpen(false);
    },
  });

  const handleProposeFromRule = (rule) => {
    setInitialForm({
      title: `Automatizar: ${rule.title}`,
      description: `Regla Zicasso: "${rule.title}"\n\n${rule.text}\n\n→ Propuesta concreta de automatización:`,
      priority: "medium",
      status: "idea",
      linked_rule: rule.title,
    });
    setDialogOpen(true);
  };

  if (bp.isLoading || !bp.data) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] space-y-4 p-4" data-testid="zicasso-best-practices">
      {/* Intro */}
      <div className="rounded-lg border bg-gradient-to-r from-orange-50 to-amber-50 p-5">
        <h1 className="mb-2 flex items-center gap-2 text-2xl font-bold text-slate-900">
          <Lightbulb className="h-6 w-6 text-amber-500" />
          Reglas y Best Practices de Zicasso
        </h1>
        <p className="max-w-3xl text-sm text-slate-700">
          Aquí tienes las normas oficiales de Zicasso para gestionar leads. Repásalas con el
          equipo y, cuando veas oportunidades de automatización, click en una regla o usa
          &quot;Nueva idea&quot; para anotarlas — te las implementaré por prioridad.
        </p>
      </div>

      <TimelineSection timeline={bp.data.timeline} />
      <RulesSection rules={bp.data.rules} onProposeAutomation={handleProposeFromRule} />
      <TemplatesSection templates={bp.data.templates} />
      <IdeasSection />

      {/* Fuente */}
      <div className="rounded-md border border-dashed p-3 text-center">
        <p className="text-[11px] text-muted-foreground">
          Fuente:{" "}
          <a
            href={bp.data.sources[0]?.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            {bp.data.sources[0]?.label}
          </a>
        </p>
      </div>

      <AutomationIdeaDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={initialForm}
        onSave={(form) => saveMut.mutate(form)}
      />
    </div>
  );
}
